import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { initialValues, validationSchema } from './formData';
import { PqrStatusManagement } from '../../../../../../shared/types/modules/pqr';
import { PqrStatusManagementProps } from './props';
import dayjs from 'dayjs';
import { ISelect, ParamEnum } from '../../../../../../shared/types/shared';
import {
    asyncGetParamPqrTimes,
    asyncGetParams,
} from '../../../../../../services/param.service';
import { calculateDateWithDays } from '../../../../../../shared/utils/date';

export const usePqrStatusManagement = ({
    data,
    onChangeData,
    onChangeFormik,
    createdAt,
    status,
    receptionChannel,
    type,
    half,
    subtypeDp,
    procedure,
    onIsWorngMovement,
}: PqrStatusManagementProps) => {
    const [originalStatusList, setOriginalStatusList] = useState<ISelect[]>([]);
    const [statusList, setStatusList] = useState<ISelect[]>([]);
    const [lastStatusParam, setLastStatusParam] = useState<ISelect>();
    const [statusParam, setStatusParam] = useState<ISelect>();
    const [types, setTypes] = useState<ISelect[]>([]);

    const onSubmit = (data: PqrStatusManagement) => {
        if (onChangeData) onChangeData(data || {});
    };

    const getStatusParamById = (id: string) =>
        statusList.find((s) => s.id === id);

    const getTypeParamByCode = (code: string) =>
        types.find((s) => s.code === code);

    const isClosingStatus = statusParam?.code === `${ParamEnum.PQR_STATUS}_CER`;
    const isFollowUpStatus =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_SEG`;
    const isStatusExtensionInfo =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_AMP`;
    const isStatusReceptionInfo =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_REC`;
    const isStatusSolution =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_SOL`;
    const isStatusOpen = statusParam?.code === `${ParamEnum.PQR_STATUS}_ABI`;
    const isStatusProcessed =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_TRA`;
    const isStatusExtension =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_PRO`;
    const isStatusCancelled =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_ANU`;
    const isTypeQOR =
        Boolean(type) &&
        type === getTypeParamByCode(`${ParamEnum.PQR_TYPE}_QOR`)?.id;

    const minClosedDate = isTypeQOR
        ? dayjs(createdAt).add(1, 'days')
        : dayjs(createdAt);

    const isStatusProcessedInFirstLoad =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_TRA` && !lastStatusParam;

    const maxClosedDate = dayjs();

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema(
            isClosingStatus,
            isFollowUpStatus,
            isStatusExtensionInfo,
            isStatusReceptionInfo,
            isStatusSolution,
            isStatusProcessed,
            isStatusExtension,
            createdAt
        ),
        onSubmit,
    });

    const maxNotificationProcedureDate =
        formik.values.notificationProcedureDate &&
        dayjs(formik.values.notificationProcedureDate).isAfter(dayjs(createdAt))
            ? dayjs(formik.values.notificationProcedureDate)
            : dayjs(createdAt);

    const minSuspensionDate = [
        formik.values.notificationProcedureDate,
        formik.values.processingDate,
        createdAt,
    ]
        .filter((date) => date)
        .map((date) => dayjs(date))
        .reduce(
            (max, date) => (date.isAfter(max) ? date : max),
            dayjs(createdAt)
        );

    const minReactivationDate = [
        minSuspensionDate,
        formik.values.suspensionDate,
    ]
        .filter((date) => date)
        .map((date) => dayjs(date))
        .reduce(
            (max, date) => (date.isAfter(max) ? date : max),
            dayjs(createdAt)
        );

    const minNotificationResponseDate = [
        minSuspensionDate,
        formik.values.responseDate,
    ]
        .filter((date) => date)
        .map((date) => dayjs(date))
        .reduce(
            (max, date) => (date.isAfter(max) ? date : max),
            dayjs(createdAt)
        );

    const minResponseDate = [
        minSuspensionDate,
        formik.values.reactivationDate,
        formik.values.extensionDate,
    ]
        .filter((date) => date)
        .map((date) => dayjs(date))
        .reduce(
            (max, date) => (date.isAfter(max) ? date : max),
            dayjs(createdAt)
        );

    const getDates = async () => {
        const statusParam = getStatusParamById(formik.values.status as string);

        const isStatusExtensionInfo =
            statusParam?.code === `${ParamEnum.PQR_STATUS}_AMP`;

        const isChangeStatus = statusParam?.id !== status;

        if (
            (data?.sacDate && data?.furagDate && !isChangeStatus) ||
            !createdAt ||
            !statusParam ||
            !receptionChannel ||
            !type ||
            !half ||
            !subtypeDp ||
            !procedure
        )
            return;

        const times = await asyncGetParamPqrTimes({
            status: formik.values.status as string,
            receptionChannel,
            type,
            half,
            subtypeDp,
            procedure,
        });
        if (!times || !times.sacTime || !times.furagTime) return;

        let daysSac = times.sacTime;
        let daysFurag = times.furagTime;

        if (isStatusExtension) {
            daysSac += 10;
            daysFurag += 10;
        }

        if (isStatusExtensionInfo) {
            daysSac = 30;
            daysFurag = 30;
        }

        const dateToCalculation = formik.values.reactivationDate
            ? dayjs(formik.values.reactivationDate).toDate()
            : dayjs(createdAt).subtract(5, 'hours').toDate();

        formik.setFieldValue(
            'sacDate',
            calculateDateWithDays(daysSac, dateToCalculation)
        );

        formik.setFieldValue(
            'furagDate',
            calculateDateWithDays(
                daysFurag,
                dateToCalculation,
                isStatusExtensionInfo,
                isStatusExtensionInfo
            )
        );
    };

    const STATUS_WORNG_MOVEMENT = [`${ParamEnum.PQR_STATUS}_AMP`];

    const dateRadication = formik.values.customerFilingDate
        ? dayjs(formik.values.customerFilingDate)
        : null;

    const daysSinceRadication = dateRadication
        ? dayjs().diff(dateRadication, 'day')
        : null;

    const isWorngMovement = Boolean(
        statusParam &&
            !data?.suspensionDate &&
            daysSinceRadication !== null &&
            daysSinceRadication > 10 &&
            STATUS_WORNG_MOVEMENT.includes(statusParam?.code || '')
    );

    useEffect(() => {
        onIsWorngMovement(isWorngMovement);
    }, [isWorngMovement]);

    useEffect(() => {
        if (onChangeFormik) onChangeFormik(formik);
    }, [formik.values, formik.errors]);

    useEffect(() => {
        formik?.setFieldValue(
            'customerFilingDate',
            data?.customerFilingDate
                ? dayjs(data?.customerFilingDate)
                : dayjs(createdAt).subtract(5, 'hours')
        );
        formik?.setFieldValue(
            'notificationProcedureDate',
            data?.notificationProcedureDate
                ? dayjs(data?.notificationProcedureDate)
                : dayjs(createdAt).subtract(5, 'hours')
        );
        formik?.setFieldValue(
            'processingDate',
            data?.processingDate ? dayjs(data?.processingDate) : undefined
        );
        formik?.setFieldValue(
            'suspensionDate',
            data?.suspensionDate ? dayjs(data?.suspensionDate) : undefined
        );
        formik?.setFieldValue(
            'sendEmailExpansionInformation',
            data?.sendEmailExpansionInformation || ''
        );
        formik?.setFieldValue(
            'reactivationDate',
            data?.reactivationDate ? dayjs(data?.reactivationDate) : undefined
        );
        formik?.setFieldValue(
            'extensionDate',
            data?.extensionDate ? dayjs(data?.extensionDate) : undefined
        );
        formik?.setFieldValue(
            'sendEmailExtension',
            data?.sendEmailExtension || ''
        );
        formik?.setFieldValue(
            'alertSFCDate',
            data?.alertSFCDate ? dayjs(data?.alertSFCDate) : undefined
        );
        formik?.setFieldValue(
            'responseDate',
            data?.responseDate ? dayjs(data?.responseDate) : undefined
        );
        formik?.setFieldValue(
            'sendEmailResponse',
            data?.sendEmailResponse || ''
        );
        formik?.setFieldValue(
            'notificationResponseDate',
            data?.notificationResponseDate
                ? dayjs(data?.notificationResponseDate)
                : undefined
        );
        formik?.setFieldValue(
            'sacDate',
            data?.sacDate ? dayjs(data?.sacDate) : undefined
        );
        formik?.setFieldValue(
            'furagDate',
            data?.furagDate ? dayjs(data?.furagDate) : undefined
        );
        formik?.setFieldValue(
            'closingDate',
            data?.closingDate ? dayjs() : undefined
        );
        formik?.setFieldValue(
            'editFuragAndSac',
            data?.editFuragAndSac || false
        );
    }, [data]);

    useEffect(() => {
        formik.setFieldValue('status', status || '');
    }, [status]);

    useEffect(() => {
        getDates();

        const asyncStatus = getStatusParamById(formik.values.status as string);
        if (statusParam && statusParam?.code !== asyncStatus?.code)
            setLastStatusParam(statusParam);
        setStatusParam(asyncStatus);
    }, [
        createdAt,
        formik.values.status,
        receptionChannel,
        type,
        half,
        subtypeDp,
        procedure,
        data,
        formik.values.reactivationDate,
    ]);

    useEffect(() => {
        asyncGetParams(ParamEnum.PQR_STATUS).then((items) => {
            setStatusList(items);
            setOriginalStatusList(items);
        });
        asyncGetParams(ParamEnum.PQR_TYPE).then((items) => setTypes(items));
    }, []);

    useEffect(() => {
        if (isClosingStatus && !formik.values.closingDate)
            formik.setFieldValue('closingDate', dayjs());
    }, [isClosingStatus]);

    const filteredStatusList = useMemo(() => {
        let updatedStatusList: ISelect[] = JSON.parse(
            JSON.stringify(originalStatusList)
        );
        let isUpdated = false;

        if (
            statusParam !== undefined &&
            statusParam?.code !== `${ParamEnum.PQR_STATUS}_REP`
        ) {
            updatedStatusList = updatedStatusList.filter(
                (statusItem) =>
                    statusItem.code !== `${ParamEnum.PQR_STATUS}_REP`
            );
            isUpdated = true;
        }

        if (!data?.suspensionDate) {
            updatedStatusList = updatedStatusList.filter(
                (statusItem) =>
                    statusItem.code !== `${ParamEnum.PQR_STATUS}_REC`
            );
            isUpdated = true;
        }

        if (isUpdated) {
            return updatedStatusList;
        }

        return originalStatusList;
    }, [statusParam, originalStatusList, data?.suspensionDate]);

    // Update statusList when filteredStatusList changes
    useEffect(() => {
        setStatusList(filteredStatusList);
    }, [filteredStatusList]);

    useEffect(() => {
        if (
            isStatusProcessed &&
            !formik.values.processingDate &&
            !isStatusProcessedInFirstLoad
        )
            formik.setFieldValue('processingDate', dayjs());
    }, [isStatusProcessed, formik.values.processingDate]);

    useEffect(() => {
        if (
            lastStatusParam?.code === `${ParamEnum.PQR_STATUS}_AMP` &&
            statusParam?.code === `${ParamEnum.PQR_STATUS}_SOL`
        ) {
            formik.setFieldValue('suspensionDate', '');
        }
    }, [lastStatusParam, statusParam]);

    return {
        formik,
        statusList,
        isClosingStatus,
        isFollowUpStatus,
        isStatusExtensionInfo,
        isStatusReceptionInfo,
        isStatusSolution,
        isStatusOpen,
        isStatusProcessed,
        isStatusExtension,
        isStatusCancelled,
        isTypeQOR,
        minClosedDate,
        maxClosedDate,
        maxNotificationProcedureDate,
        minSuspensionDate,
        minReactivationDate,
        minNotificationResponseDate,
        minResponseDate,
        isStatusProcessedInFirstLoad,
        isWorngMovement,
    };
};
